/**
 * Payments API client.
 *
 * Kept in its own module rather than added to the ~5k-line api.ts so the
 * payment surface stays reviewable on its own.
 *
 * Note what none of these calls send: an **amount**. The server prices every
 * order from its own catalogue. A client-supplied amount is the single worst
 * vulnerability available in a payments integration, so the field does not
 * exist in any request type.
 */

import { AxiosError } from "axios";

import { authAxios } from "@/services/axiosInstance";
import { getEnvVar } from "@/utils/util";
import type {
  CouponValidation,
  MyPaymentsResponse,
  PaymentConfig,
  PaymentErrorCode,
  PaymentIntentResult,
  PaymentPurpose,
  PaymentStatusResult,
  Quote,
  SponsorshipValidation,
} from "@/types/payments";

const API_BASE = getEnvVar("API_BASE");

export interface CreateIntentPayload {
  purpose: PaymentPurpose;
  programId?: number | null;
  couponCode?: string | null;
  /**
   * Organisation sponsorship code. Re-validated server-side when the order is
   * priced — sending it here claims nothing on its own.
   */
  sponsorshipCode?: string | null;
}

export const paymentsApi = {
  /** Publishable key + kill-switch state. Environment-derived, not build-baked. */
  getConfig: async (): Promise<PaymentConfig> => {
    const { data } = await authAxios.get(`${API_BASE}/payments/config`);
    return data;
  },

  /**
   * Price an order. Read-only: reserves nothing and charges nothing, so it is
   * safe to call on every change to the coupon field.
   */
  getQuote: async (
    purpose: PaymentPurpose,
    programId?: number | null,
    couponCode?: string | null,
    sponsorshipCode?: string | null,
  ): Promise<Quote> => {
    const params = new URLSearchParams({ purpose });
    if (programId != null) params.set("program_id", String(programId));
    if (couponCode) params.set("coupon_code", couponCode);
    if (sponsorshipCode) params.set("sponsorship_code", sponsorshipCode);
    const { data } = await authAxios.get(`${API_BASE}/payments/quote?${params}`);
    return data;
  },

  /** Validate a coupon and preview its discount. Rate-limited per user. */
  validateCoupon: async (
    code: string,
    purpose: PaymentPurpose,
    programId?: number | null,
  ): Promise<CouponValidation> => {
    const { data } = await authAxios.post(`${API_BASE}/payments/coupons/validate`, {
      code,
      purpose,
      programId: programId ?? null,
    });
    return data;
  },

  /**
   * Validate an organisation sponsorship code and preview the covered total.
   *
   * Grants nothing — the sponsorship is applied as a 100% discount when the
   * order is priced, so a sponsored board still goes through the normal
   * pay-and-settle flow and leaves a real payment record behind.
   *
   * Rate-limited per user (server-side, Redis) because a sponsorship code is a
   * guessable secret that covers the whole board.
   */
  validateSponsorship: async (
    code: string,
    purpose: PaymentPurpose,
    programId?: number | null,
  ): Promise<SponsorshipValidation> => {
    const { data } = await authAxios.post(`${API_BASE}/payments/sponsorship/validate`, {
      code,
      purpose,
      programId: programId ?? null,
    });
    return data;
  },

  /**
   * Create — or return the existing — PaymentIntent for this scope.
   *
   * Idempotent by design: a double-click returns the SAME payment and client
   * secret rather than charging twice, so the caller does not need to guard
   * against repeat submissions for correctness (only for UX).
   */
  createIntent: async (payload: CreateIntentPayload): Promise<PaymentIntentResult> => {
    const { data } = await authAxios.post(`${API_BASE}/payments/intents`, {
      purpose: payload.purpose,
      programId: payload.programId ?? null,
      couponCode: payload.couponCode ?? null,
      sponsorshipCode: payload.sponsorshipCode ?? null,
    });
    return data;
  },

  /** Re-price an open payment after the coupon or sponsorship changed. */
  refreshIntent: async (
    externalId: string,
    payload: CreateIntentPayload,
  ): Promise<PaymentIntentResult> => {
    const { data } = await authAxios.post(
      `${API_BASE}/payments/intents/${externalId}/refresh`,
      {
        purpose: payload.purpose,
        programId: payload.programId ?? null,
        couponCode: payload.couponCode ?? null,
        sponsorshipCode: payload.sponsorshipCode ?? null,
      },
    );
    return data;
  },

  /** User backed out: cancels at Stripe and releases any coupon hold. */
  cancelIntent: async (externalId: string): Promise<PaymentStatusResult> => {
    const { data } = await authAxios.post(`${API_BASE}/payments/intents/${externalId}/cancel`);
    return data;
  },

  /**
   * Authoritative payment status.
   *
   * The success screen polls this instead of trusting Stripe's redirect
   * parameters: after confirmPayment resolves, the browser knows the charge
   * succeeded but our database may not yet. This endpoint is allowed a high
   * rate limit (120/min) precisely because it is polled.
   */
  getStatus: async (externalId: string): Promise<PaymentStatusResult> => {
    const { data } = await authAxios.get(`${API_BASE}/payments/${externalId}`);
    return data;
  },

  /** Pending payments + entitlements — drives resume routing and PaymentGuard. */
  getMyPayments: async (): Promise<MyPaymentsResponse> => {
    const { data } = await authAxios.get(`${API_BASE}/payments/entitlements/me`);
    return data;
  },
};

/**
 * Pull the backend's stable `error_code` out of an Axios failure.
 *
 * Always prefer this over matching on message text — messages are copy and
 * will change; codes are contract.
 */
export function paymentErrorCode(err: unknown): PaymentErrorCode | null {
  if (err instanceof AxiosError) {
    const code = err.response?.data?.error_code;
    if (typeof code === "string") return code as PaymentErrorCode;
  }
  return null;
}

/** Human-readable message for a failure, falling back to the given default. */
export function paymentErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const detail = err.response?.data?.detail;
    if (typeof detail === "string" && detail) return detail;
  }
  return fallback;
}

/**
 * Where the server says to go to satisfy a 402.
 *
 * `PAYMENT_REQUIRED` responses carry the destination, so the client never has
 * to construct checkout URLs itself.
 */
export function paymentRequiredRedirect(err: unknown): string | null {
  if (err instanceof AxiosError && err.response?.status === 402) {
    const url = err.response?.data?.checkoutUrl;
    if (typeof url === "string" && url) return url;
  }
  return null;
}

/**
 * The numeric program id a 402 refers to, for opening payment in place.
 *
 * Public program payloads expose only `externalId`, but the payment scope is
 * keyed on the internal numeric id -- and the 402 already carries it, so this
 * avoids leaking numeric ids into the public program schema just to pay.
 */
export function paymentRequiredProgramId(err: unknown): number | null {
  if (err instanceof AxiosError && err.response?.status === 402) {
    const id = err.response?.data?.programId;
    if (typeof id === "number") return id;
  }
  return null;
}
